import React, { useContext, useState, useRef, useLayoutEffect } from 'react';
import { useContext as useReactContext } from 'react';
import { TechnologyContext } from 'context/TechnologyContext';
import { BetweenVerticalStart, CircleX, FileStack, GitBranchPlus, Waypoints, ZoomIn } from 'lucide-react';
import { useGant } from './useGant';
import { updateAddPrzerwaGant } from 'actions/updateAddPrzerwaGant';
import { updateDeletePrzerwaGant } from 'actions/updateDeletePrzerwaGant';

// Wspólne style dla przycisków menu
const buttonStyle = {
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
};

const GanttContextMenu = ({ setModalGrupyOpen, contextMenu, onClose, handleCenterOnStage }) => {
  const { fechparametryTechnologii } = useContext(TechnologyContext);
  const { refreshGantAll, generujPolaczenia } = useGant();

  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });

  // Obliczanie poprawnej pozycji menu przed wyrenderowaniem na ekranie
  useLayoutEffect(() => {
    if (!contextMenu || !menuRef.current) return;

    const { x, y } = contextMenu;
    const { offsetWidth: menuWidth, offsetHeight: menuHeight } = menuRef.current;
    const { innerWidth: windowWidth, innerHeight: windowHeight } = window;

    const margin = 10; // odstęp od krawędzi ekranu

    let newX = x;
    let newY = y;

    // Korekta w osi X (prawa krawędź)
    if (x + menuWidth > windowWidth - margin) {
      newX = windowWidth - menuWidth - margin;
    }

    // Korekta w osi Y (dolna krawędź)
    if (y + menuHeight > windowHeight - margin) {
      newY = windowHeight - menuHeight - margin;
    }

    // Zabezpieczenie przed ucieczką poza górną lub lewą krawędź
    newX = Math.max(margin, newX);
    newY = Math.max(margin, newY);

    setAdjustedPosition({ x: newX, y: newY });
  }, [contextMenu]);

  if (!contextMenu) return null;

  const { stage } = contextMenu;
  const isBreak = stage.typ_grupy !== 2;

  // Definicje akcji dla procesów (typ_grupy == 2)
  const processMenuItems = [
    {
      label: "Arkusze",
      icon: <FileStack size={20} style={{ marginRight: "15px" }} />,
      disabled: stage.proces_nazwa_id === 6,
      onClick: () => {
        setModalGrupyOpen(true);
        fechparametryTechnologii(stage.zamowienie_id, stage.technologia_id);
      },
    },
    {
      label: "Dodaj przerwę",
      icon: <BetweenVerticalStart size={20} style={{ marginRight: "15px", marginBottom: "5px", color: "yellowgreen" }} />,
      onClick: () => updateAddPrzerwaGant(stage.global_id, 480, refreshGantAll, stage.proces_nazwa_id),
      textColor: "#aaaaaa",
    },
    {
      label: "Zoom x 0.25",
      icon: <ZoomIn size={20} style={{ marginRight: "15px" }} />,
      onClick: () => handleCenterOnStage(stage, 0.25),
    },
    {
      label: "Zoom x 1.0",
      icon: <ZoomIn size={20} style={{ marginRight: "15px" }} />,
      onClick: () => handleCenterOnStage(stage, 1.0),
    },
    {
      label: "Zoom x 2.0",
      icon: <ZoomIn size={20} style={{ marginRight: "15px" }} />,
      onClick: () => handleCenterOnStage(stage, 2.0),
    },
    {
      label: "Wygeneruj połączenia",
      icon: <GitBranchPlus size={20} style={{ marginRight: "15px", color: "yellowgreen" }} />,
      onClick: () => generujPolaczenia(stage.technologia_id),
    },
  ];

  // Definicje akcji dla przerwy
  const breakMenuItems = [
    {
      label: "Dodaj przerwę",
      icon: <BetweenVerticalStart size={20} style={{ marginRight: "15px", marginBottom: "5px", color: "yellowgreen" }} />,
      onClick: () => updateAddPrzerwaGant(stage.global_id, 480, refreshGantAll, stage.proces_nazwa_id),
    },
    {
      label: "Skasuj przerwę",
      icon: <CircleX size={20} style={{ marginRight: "15px", marginBottom: "5px", color: "red" }} />,
      onClick: () => updateDeletePrzerwaGant(stage.global_id, refreshGantAll, stage.proces_nazwa_id),
    },
  ];

  const currentMenuItems = isBreak ? breakMenuItems : processMenuItems;

  return (
    <>
      {/* Przezroczysta warstwa blokująca */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 999,
          background: "transparent",
        }}
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />

      {/* Właściwe menu kontekstowe */}
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          top: adjustedPosition.y,
          left: adjustedPosition.x,
          background: "#2b2b2b",
          color: "#ffffff",
          border: "1px solid #444",
          borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          zIndex: 1000,
          padding: "6px 0",
          minWidth: "160px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "10px 22px",
            fontSize: "12px",
            borderBottom: "1px solid #444",
            color: "#aaa",
          }}
        >
          {!isBreak ? (
            <>
              {stage.nr}_{stage.rok?.substring(2, 4)} {stage.firma_nazwa} {stage.tytul}
            </>
          ) : (
            "Przerwa"
          )}
        </div>

        {currentMenuItems.map((item, index) => {
          const isDisabled = item.disabled;
          return (
            <button
              key={index}
              disabled={isDisabled}
              style={{
                ...buttonStyle,
                color: isDisabled ? '#aaaaaa42' : (item.textColor || '#aaaaaa'),
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#3a3a3a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              onClick={() => {
                if (!isDisabled) {
                  item.onClick();
                  onClose();
                }
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
};

export default GanttContextMenu;